import type { TFunction } from 'i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import type { LocalizedListingContent, LocalizedTaxInfo, LocalizedEvaluationInfo } from 'data/listing';
import type { KeyValueItem } from '../../types';

/**
 * Locale-data tax fields (structured `{ year, amount }`), rendered as formatted currency + year.
 */
type TaxFieldKey = 'municipalTaxes' | 'schoolTaxes' | 'waterTaxes' | 'utilitiesTaxes' | 'otherTaxes';

/**
 * String-valued locale-data fields. Excludes `description`, `extras` (inclusions), `exclusions`,
 * `isAiTranslated`, and `evaluation` — those are rendered elsewhere in the Property Details tab.
 */
type StringFieldKey = Exclude<
  keyof LocalizedListingContent,
  TaxFieldKey | 'description' | 'extras' | 'exclusions' | 'rooms' | 'evaluation' | 'isAiTranslated'
>;

/** String fields shown in the Additional Information section, in display order. */
const ADDITIONAL_DETAILS_STRING_FIELDS: ReadonlyArray<readonly [StringFieldKey, string]> = [
  ['type', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_TYPE],
  ['style', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_STYLE],
  ['floors', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_FLOORS],
  ['dimensions', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_DIMENSIONS],
  ['proximity', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_PROXIMITY],
  ['zoning', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_ZONING],
  // ['parking', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_PARKING],
  ['driveway', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_DRIVEWAY],
  // ['garage', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_GARAGE],
  // ['basement', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_BASEMENT],
  ['roofing', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_ROOFING],
  ['siding', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_SIDING],
  ['windows', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_WINDOWS],
  ['windowType', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_WINDOW_TYPE],
  ['waterSupply', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_WATER_SUPPLY],
  ['sewageSystem', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_SEWAGE_SYSTEM],
  ['heatingEnergy', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_HEATING_ENERGY],
  ['heatingSystem', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_HEATING_SYSTEM],
  ['energyEfficiency', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_ENERGY_EFFICIENCY],
  ['bathroom', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_BATHROOM],
  ['cupboard', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_CUPBOARD],
  ['rentalAppliances', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_RENTAL_APPLIANCES],
  ['equipmentAvailable', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_EQUIPMENT_AVAILABLE],
  ['landscaping', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_LANDSCAPING],
  ['topography', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_TOPOGRAPHY],
  ['distinctiveFeatures', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_DISTINCTIVE_FEATURES],
];

/** Tax fields shown in the Additional Information section, in display order. */
const ADDITIONAL_DETAILS_TAX_FIELDS: ReadonlyArray<readonly [TaxFieldKey, string]> = [
  ['municipalTaxes', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_MUNICIPAL_TAXES],
  ['schoolTaxes', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_SCHOOL_TAXES],
  ['waterTaxes', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_WATER_TAXES],
  ['utilitiesTaxes', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_UTILITIES_TAXES],
  ['otherTaxes', LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_OTHER_TAXES],
];

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Formats a raw monetary amount as a `$`-prefixed, comma-grouped value. Centris amounts arrive as
 * plain digit strings (e.g. `"435500"` -> `"$435,500"`); an already-formatted value like `"$3,200"`
 * is idempotent. Returns the trimmed original when the value is non-numeric so nothing is lost.
 */
function formatCurrencyAmount(value: string | null | undefined): string | null {
  if (!isNonEmptyString(value)) {
    return null;
  }
  const numeric = Number(value.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return value.trim();
  }
  return `$${numeric.toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
}

function formatTax(tax: LocalizedTaxInfo | null | undefined, t: TFunction): string | null {
  const amount = formatCurrencyAmount(tax?.amount);
  if (!amount) {
    return null;
  }
  if (isNonEmptyString(tax?.year)) {
    return t(LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_TAX_AMOUNT_WITH_YEAR, { amount, year: tax.year });
  }
  return amount;
}

/** Builds the municipal-evaluation rows (year, land, building), skipping any empty value. */
function buildEvaluationFields(evaluation: LocalizedEvaluationInfo | null | undefined): KeyValueItem[] {
  if (!evaluation) {
    return [];
  }

  const fields: KeyValueItem[] = [];

  if (isNonEmptyString(evaluation.year)) {
    fields.push({ label: LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_EVALUATION_YEAR, value: evaluation.year.trim() });
  }

  const lot = formatCurrencyAmount(evaluation.lot);
  if (lot) {
    fields.push({ label: LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_LOT_EVALUATION, value: lot });
  }

  const building = formatCurrencyAmount(evaluation.building);
  if (building) {
    fields.push({ label: LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_BUILDING_EVALUATION, value: building });
  }

  return fields;
}

/**
 * Builds the ordered label/value list for the address page "Additional Information" section
 * from a listing's localized content (`locale_data`). Only fields with a non-empty value are
 * included. `description`, `extras` (inclusions) and `exclusions` are intentionally omitted.
 */
export function buildAdditionalDetailsFields(
  content: LocalizedListingContent | null | undefined,
  t: TFunction
): KeyValueItem[] {
  if (!content) {
    return [];
  }

  const fields: KeyValueItem[] = [];

  for (const [key, label] of ADDITIONAL_DETAILS_STRING_FIELDS) {
    const value = content[key];
    if (isNonEmptyString(value)) {
      fields.push({ label, value });
    }
  }

  for (const [key, label] of ADDITIONAL_DETAILS_TAX_FIELDS) {
    const formatted = formatTax(content[key], t);
    if (formatted) {
      fields.push({ label, value: formatted });
    }
  }

  fields.push(...buildEvaluationFields(content.evaluation));

  return fields;
}
